<template>
  <b-card>
    <div slot="header">
      <i class='fa fa-align-justify'></i> Repeat Orders - Payment Tracking
    </div>

    <p class="text-muted">
      Monitor repeat cycles, payment retries, and failures requiring manual recovery.
      Select a row to view the full event log, or use <strong>Cancel repeat</strong> when a patient
      no longer needs the cycle (reason required; patient is emailed). Failed charges retry automatically up to
      3 times at 24-hour intervals; after the third failure the cycle is escalated here
      for manual recovery.
    </p>

    <b-row class="align-items-center mb-3">
      <b-col sm="12" md="4" class="mb-2 mb-md-0">
        <b-input-group>
          <b-form-input type="search" debounce="500" v-model="searchQuery"
            placeholder="Search by patient name or order ID"
            aria-label="Search by patient name or order ID" />
          <b-input-group-append>
            <b-button variant="primary" :disabled="!searchQuery" @click="currentPage = 1">
              <i class="fa fa-search"></i>
            </b-button>
          </b-input-group-append>
        </b-input-group>
      </b-col>
      <b-col sm="12" md="8" class="text-md-right">
        <b-button v-for="chip in statusChips" :key="chip.label" pill size="sm" class="ml-1 mb-1"
          :variant="activeStatus === chip.key ? 'primary' : 'outline-secondary'"
          @click="setStatusFilter(chip.key)">
          {{ chip.label }}
        </b-button>
      </b-col>
    </b-row>

    <div v-if="loading" class="loadingOrders">Loading...</div>

    <template v-else>
      <b-row>
        <b-col>
          <b-table class="repeat-cycles-table" :items="filteredRows" :fields="columnNames"
            :current-page="currentPage" :per-page="perPage" hover outlined show-empty responsive
            @row-clicked="openCycle">
            <template v-slot:cell(progress)="row">
              <div v-if="row.item.total" class="d-flex align-items-center">
                <b-progress class="w-50 mr-2" :value="row.item.issued" :max="row.item.total" variant="success" />
                <span class="text-nowrap">{{ row.item.issued }} of {{ row.item.total }}</span>
              </div>
              <span v-else class="text-muted">—</span>
            </template>
            <template v-slot:cell(status)="row">
              <b-badge pill :variant="statusVariant(row.item.displayStatus)">{{ row.item.displayStatus | repeatCycleStatus }}</b-badge>
            </template>
            <template v-slot:cell(actions)="row">
              <b-button
                v-if="row.item.canCancel"
                size="sm"
                variant="outline-danger"
                @click.stop="openCancel(row.item)"
              >
                Cancel repeat
              </b-button>
              <span v-else class="text-muted">—</span>
            </template>
            <template v-slot:empty>
              <div class="text-center text-muted my-4">
                <template v-if="searchQuery || activeStatus">
                  <p class="h4 mb-2"><i class="fa fa-search"></i></p>
                  <p class="font-weight-bold text-dark mb-1">No results found</p>
                  <p class="mb-3">
                    No repeat cycles{{ searchQuery ? ` match “${searchQuery}”` : '' }}{{ activeStatus ? `${searchQuery ? ' with' : ' have'} status ${activeChipLabel}` : '' }}.
                  </p>
                  <b-button v-if="searchQuery" variant="outline-secondary" size="sm" class="mx-1"
                    @click="searchQuery = ''">Clear search</b-button>
                  <b-button v-if="activeStatus" variant="outline-secondary" size="sm" class="mx-1"
                    @click="setStatusFilter(null)">Show all statuses</b-button>
                </template>
                <template v-else>
                  <p class="font-weight-bold text-dark mb-1">No repeat cycles yet</p>
                  <p>Repeat cycles will appear here once clinicians approve repeat prescriptions for patients.</p>
                </template>
              </div>
            </template>
          </b-table>
        </b-col>
      </b-row>

      <b-row v-if="filteredRows.length > perPage" class="align-items-center">
        <b-col sm="12" md="8">
          <b-pagination :total-rows="filteredRows.length" :per-page="perPage" v-model="currentPage"
            :first-text="'First'" :prev-text="'Prev'" :next-text="'Next'" :last-text="'Last'" />
        </b-col>
        <b-col sm="12" md="4" class="text-md-right text-muted">
          Showing {{ showingFrom }} to {{ showingTo }} of {{ filteredRows.length }}
        </b-col>
      </b-row>
      <b-row v-else-if="filteredRows.length > 0">
        <b-col class="text-md-right text-muted">
          Showing {{ filteredRows.length }} of {{ filteredRows.length }}
        </b-col>
      </b-row>
    </template>

    <admin-cycle-detail-modal v-model="showDetail" :order-item-id="selectedOrderItemId"
      :cycle="selectedCycle" @refresh="loadCycles" />

    <cancel-repeat-prescription-modal
      v-if="cancelOrderItemId"
      v-model="showCancelModal"
      :order-item-id="cancelOrderItemId"
      :patient-label="cancelPatientLabel"
      @success="onCancelSuccess"
    />
  </b-card>
</template>

<script>

import moment from 'moment';
import AdminCycleDetailModal from '../../components/AdminCycleDetailModal';
import CancelRepeatPrescriptionModal from '../../components/CancelRepeatPrescriptionModal';
import {
  adminDisplayStatus,
  canCancelRepeatCycle,
  failedAttemptsForCurrentFlow,
  isAwaitingPayment,
  issuedRepeats,
  repeatStatusVariant,
  RETRY_LIMIT
} from '../../pipes/RepeatCycleStatusPipe';

export default {
  components: {
    AdminCycleDetailModal,
    CancelRepeatPrescriptionModal
  },
  data: function () {
    return {
      cycles: [],
      loading: false,
      searchQuery: '',
      activeStatus: null,
      currentPage: 1,
      perPage: 5,
      showDetail: false,
      selectedOrderItemId: null,
      selectedCycle: null,
      statusChips: [
        { key: null, label: 'All' },
        { key: 'PAYMENT_FAILED', label: 'Payment Failed - action required' },
        { key: 'PENDING_REVIEW', label: 'Pending Review' },
        { key: 'UPCOMING', label: 'Upcoming' },
        { key: 'COMPLETED', label: 'Completed' },
        { key: 'CANCELLED', label: 'Cancelled' }
      ],
      columnNames: [
        { key: 'orderItemId', label: 'Order' },
        { key: 'patient', label: 'Patient' },
        { key: 'medication', label: 'Medication' },
        { key: 'progress', label: 'Progress' },
        { key: 'status', label: 'Status' },
        { key: 'attempts', label: 'Payment attempts' },
        { key: 'nextAction', label: 'Next scheduled action' },
        { key: 'actions', label: 'Actions', class: 'text-nowrap' }
      ],
      showCancelModal: false,
      cancelOrderItemId: null,
      cancelPatientLabel: ''
    }
  },
  computed: {
    filteredRows () {
      let rows = this.cycles;
      if (this.activeStatus) {
        rows = rows.filter(r => r.displayStatus === this.activeStatus);
      }
      if (this.searchQuery) {
        const q = this.searchQuery.trim().toLowerCase();
        rows = rows.filter(r =>
          String(r.patient || '').toLowerCase().includes(q) ||
          String(r.orderItemId || '').includes(q));
      }
      return rows;
    },
    activeChipLabel () {
      const chip = this.statusChips.find(c => c.key === this.activeStatus);
      return chip ? chip.label : '';
    },
    showingFrom () {
      return (this.currentPage - 1) * this.perPage + 1;
    },
    showingTo () {
      return Math.min(this.currentPage * this.perPage, this.filteredRows.length);
    }
  },
  watch: {
    searchQuery: function () {
      this.currentPage = 1;
    }
  },
  created () {
    this.loadCycles();
  },
  methods: {
    statusVariant: repeatStatusVariant,
    loadCycles () {
      this.loading = true;
      this.$repeatPrescriptionService.listRepeatPrescriptions('ALL').then(list => {
        // The list endpoint returns slim rows; status, attempts and next-action
        // columns need the per-item detail (same enrichment the doctor portal does).
        return Promise.all(list.map(row =>
          this.$repeatPrescriptionService.getRepeatPrescriptionDetail(row.orderItemId)
            .then(detail => this.buildRow(Object.assign({}, row, detail || {})))
            .catch(() => this.buildRow(row))
        ));
      }).then(rows => {
        this.cycles = rows;
        this.loading = false;
      }).catch(error => {
        console.error(error);
        this.loading = false;
        this.$awn.alert('There was an error getting repeat cycles');
      });
    },
    buildRow (item) {
      const total = item.totalRepeatFlow || 0;
      const issued = issuedRepeats(item);
      const displayStatus = adminDisplayStatus(item);
      return {
        orderItemId: item.orderItemId,
        patient: item.patientName,
        medication: item.medicationPRV,
        issued: issued,
        total: total,
        displayStatus: displayStatus,
        processStatus: item.processStatus,
        currentRepeatFlow: item.currentRepeatFlow,
        attempts: this.attemptsSummary(item),
        nextAction: this.nextScheduledAction(item, displayStatus),
        nextDue: item.nextScheduledDate || null,
        canCancel: canCancelRepeatCycle(item.processStatus),
        _rowVariant: displayStatus === 'PAYMENT_FAILED' ? 'danger' : null
      };
    },
    openCancel (row) {
      this.cancelOrderItemId = row.orderItemId;
      this.cancelPatientLabel = row.patient || '';
      this.showCancelModal = true;
    },
    onCancelSuccess () {
      this.cancelOrderItemId = null;
      this.$awn.success('Repeat prescription cancelled. The patient has been notified.');
      this.loadCycles();
    },
    attemptsSummary (item) {
      if (item.processStatus !== 'PROCESSING' && !isAwaitingPayment(item)) {
        return '—';
      }
      const failed = failedAttemptsForCurrentFlow(item);
      return failed ? `${failed} of ${RETRY_LIMIT} failed` : '—';
    },
    nextScheduledAction (item, displayStatus) {
      const next = item.nextScheduledDate
        ? moment(item.nextScheduledDate, 'YYYY-MM-DD').format('DD MMM YYYY')
        : null;
      if (displayStatus === 'PAYMENT_FAILED') {
        return 'Manual recovery - admin';
      }
      if (item.processStatus === 'PROCESSING' || isAwaitingPayment(item)) {
        const failed = failedAttemptsForCurrentFlow(item);
        if (!failed) {
          // Reviewed but never charged — nothing has been retried yet.
          return `Payment due${next ? ' - ' + next : ''}`;
        }
        const retryNo = Math.min(Math.max(failed, 1), 3);
        return `Retry ${retryNo} of 3${next ? ' - ' + next : ''}`;
      }
      if (displayStatus === 'PENDING_REVIEW' || displayStatus === 'UPCOMING') {
        return `Clinician review${next ? ' - due ' + next : ''}`;
      }
      return '—';
    },
    setStatusFilter (key) {
      this.activeStatus = key;
      this.currentPage = 1;
    },
    openCycle (row) {
      this.selectedOrderItemId = row.orderItemId;
      this.selectedCycle = row;
      this.showDetail = true;
    }
  }
}

</script>

<style lang="scss" scoped>
.repeat-cycles-table ::v-deep tbody tr {
  cursor: pointer;
}
</style>
